import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import './NotificationItem.css';

/**
 * NotificationItem Component
 * Single notification item
 */
function NotificationItem({ notification, onClick }) {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const location = useLocation();
  const { markAsRead, dismissNotification } = useNotifications();

  // Detect if we're in the attorney dashboard
  const isAttorneyDashboard = location.pathname.startsWith('/attorney');
  const messagesPath = isAttorneyDashboard ? '/attorney/client-messages' : '/dashboard#messages';

  const handleClick = async () => {
    // Mark as read
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    // Navigate to messages page
    navigate(messagesPath);

    if (onClick) {
      onClick();
    }
  };

  const handleDismiss = async (e) => {
    e.stopPropagation();
    await dismissNotification(notification.id);
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('notifications.time.justNow', 'Just now');
    if (diffMins < 60) return t('notifications.time.minutesAgo', '{{count}} min ago', { count: diffMins });
    if (diffHours < 24) return t('notifications.time.hoursAgo', '{{count}} hr ago', { count: diffHours });
    if (diffDays < 7) return t('notifications.time.daysAgo', '{{count}} days ago', { count: diffDays });
    return date.toLocaleDateString();
  };

  const getUrgencyClass = () => {
    switch (notification.urgency) {
      case 'critical':
        return 'urgency-critical';
      case 'high':
        return 'urgency-high';
      case 'medium':
        return 'urgency-medium';
      default:
        return 'urgency-low';
    }
  };

  const getCategoryIcon = () => {
    switch (notification.category) {
      case 'status_change':
        return '📋';
      case 'office_action':
        return '⚠️';
      case 'filing_update':
        return '📝';
      case 'deadline':
        return '⏰';
      case 'correspondence':
        return '📧';
      case 'approval_required':
        return '✋';
      case 'task_approved':
        return '✅';
      default:
        return '🔔';
    }
  };

  return (
    <li
      className={`notification-item ${notification.read ? 'read' : 'unread'} ${getUrgencyClass()}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => e.key === 'Enter' && handleClick()}
    >
      <div className="notification-icon">
        {getCategoryIcon()}
      </div>

      <div className="notification-content">
        <div className="notification-header">
          <span className="notification-title">{notification.title}</span>
          <span className="notification-time">{getTimeAgo(notification.createdAt)}</span>
        </div>
        <p className="notification-message">{notification.message}</p>
      </div>

      <button
        className="notification-dismiss"
        onClick={handleDismiss}
        aria-label={t('notifications.dismiss', 'Dismiss')}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      {!notification.read && <span className="notification-unread-dot" />}
    </li>
  );
}

export default NotificationItem;
